
import { Canvas, Shadow, Path } from "fabric";
import { createMeasuredRectangle } from "./rectangle-utils";
import { createMeasuredCircle } from "./circle-utils";
import { createMeasuredLine } from "./line-utils";
import { createMeasuredRhomboid } from "./polygon-utils";
import { ShapeType } from "@/types/fabric";

// Explicitly define the allowed shape types
type CreateShapeType = 'square' | 'circle' | 'rhomboid' | 'rectangle' | 'oval' | 'line';

const getCommonShapeProps = (canvas: Canvas) => ({
  left: canvas.width! / 2 - 50,
  top: canvas.height! / 2 - 50,
  fill: 'rgba(30, 64, 175, 0.03)', // Slightly more visible blue fill
  stroke: '#1E40AF', // Consistent blue stroke
  strokeWidth: 2.5, // Increased thickness for better visibility
  strokeUniform: true, // Keeps stroke width consistent during scaling
  shadow: new Shadow({
    color: 'rgba(255, 255, 255, 0.8)', // White glow for contrast
    blur: 4, // Increased blur for better glow effect
    offsetX: 0,
    offsetY: 0
  }),
  cornerColor: '#1E40AF', // Blue corners
  cornerStrokeColor: '#FFFFFF', // White stroke around corners
  cornerSize: 7, // Smaller corner controls for precision
  cornerStyle: 'rect', // Square controls like in the reference image
  transparentCorners: false, // Solid corners for better visibility
  borderColor: '#1E40AF',
  borderScaleFactor: 1, // Thinner selection border
  borderDashArray: [2, 2], // Finer dashed border for professional look
});

// Get a descriptive name for each shape type
const getShapeName = (shapeType: CreateShapeType): string => {
  switch (shapeType) {
    case 'square': return 'Square';
    case 'rectangle': return 'Rectangle';
    case 'circle': return 'Circle';
    case 'oval': return 'Oval';
    case 'line': return 'Line';
    case 'rhomboid': return 'Rhomboid';
    default: {
      // Make TypeScript happy by ensuring shapeType is treated as a string
      const typeString = String(shapeType);
      return typeString.charAt(0).toUpperCase() + typeString.slice(1);
    }
  }
};

export const createShape = (canvas: Canvas, shapeType: CreateShapeType) => {
  if (!canvas) return;

  const commonProps = getCommonShapeProps(canvas);
  const shapeName = getShapeName(shapeType);
  
  let shape;

  switch (shapeType) {
    case 'square':
      shape = createMeasuredRectangle(canvas, 100, 100, {
        ...commonProps,
        rx: 0, // No rounded corners for square
        ry: 0,
      }, 'square');
      break;

    case 'rectangle':
      shape = createMeasuredRectangle(canvas, 150, 100, {
        ...commonProps,
        rx: 0, // No rounded corners for professional look
        ry: 0,
      }, 'rectangle');
      break;

    case 'circle':
      shape = createMeasuredCircle(canvas, 50, commonProps, false);
      break;

    case 'oval':
      shape = createMeasuredCircle(canvas, 50, {
        ...commonProps,
        skewX: 0, // Ensure proper oval rendering
      }, true);
      break;

    case 'line':
      shape = createMeasuredLine(canvas, [0, 0, 100, 0] as [number, number, number, number], {
        ...commonProps,
        left: canvas.width! / 2 - 50,
        top: canvas.height! / 2,
        strokeWidth: 3, // Even thicker for lines since they're 1D
        strokeLineCap: 'square', // Square line ends for technical look
      });
      break;

    case 'rhomboid': {
      const rhomboidPoints = [
        { x: 30, y: 0 },
        { x: 130, y: 0 },
        { x: 100, y: 100 },
        { x: 0, y: 100 }
      ];
      // Use the specialized rhomboid function instead of the generic polygon function
      shape = createMeasuredRhomboid(canvas, rhomboidPoints, commonProps);
      break;
    }
  }

  // Set descriptive name for the shape if it was created
  if (shape && shape.data) {
    shape.data.name = shapeName;
  }

  return shape;
};
